import { SEOHead } from '../components/SEOHead';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ScrollReveal';
import { CTABanner } from '../components/CTABanner';
import { ServiceFAQ } from '../components/ServiceFAQ';
import { Check } from 'lucide-react';

const whatWeProduce = [
  'Creator-shot product videos and unboxing content',
  'Testimonial and review-style videos',
  'Lifestyle and usage content',
  'Instagram Reels-ready short form UGC',
];

const process = [
  { num: '01', title: 'Brief', desc: 'We define messaging, hooks, and content direction.' },
  { num: '02', title: 'Creator Matching', desc: 'We source creators who fit your brand.' },
  { num: '03', title: 'Shoot', desc: 'Creators produce content with your product.' },
  { num: '04', title: 'Delivery', desc: 'Edited, ready-to-use files with usage rights.' },
];

const faqs = [
  { question: 'What is UGC content?', answer: 'UGC (User-Generated Content) is content created by real people rather than the brand itself. It includes product reviews, unboxing videos, testimonials, and lifestyle content that feels authentic and relatable. UGC is highly effective in advertising because consumers trust real people over polished brand content.' },
  { question: 'How is real UGC different from AI UGC?', answer: 'Real UGC is shot by actual creators using your product — it carries the genuine human element of a real person interacting with your brand. AI UGC is generated using AI tools to simulate the UGC aesthetic. Both work well; real UGC is better for trust-building while AI UGC is faster and cheaper to scale.' },
  { question: 'Who owns the rights to the UGC content?', answer: 'You do. All content produced through our campaigns comes with full usage rights. You can use the content across paid ads, organic posts, your website, and any other marketing channel without additional licensing fees.' },
  { question: 'How many creators do you work with per campaign?', answer: 'This depends on your campaign goals and budget. A typical campaign involves 5-15 creators. We recommend variety in creator profiles to test different audiences and content styles. We handle all sourcing, briefing, and coordination.' },
  { question: 'Can we use UGC content in paid ads?', answer: 'Absolutely. In fact, UGC content consistently outperforms polished brand content in paid ads. UGC ads typically see lower CPMs, higher engagement rates, and better ROAS on platforms like Meta and Instagram. All content we deliver is optimized for paid ad use.' },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Real UGC Content",
  "provider": { "@type": "Organization", "name": "Thriveit Media", "url": "https://thriveitmedia.in" },
  "description": "Authentic creator-produced UGC content for D2C brands. Product videos, testimonials, and lifestyle content that converts.",
  "areaServed": "Worldwide"
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": { "@type": "Answer", "text": f.answer }
  }))
};

export default function RealUGCPage() {
  return (
    <>
      <SEOHead
        title="Real UGC Content Agency | UGC Ads for D2C Brands | Thriveit Media"
        description="Thriveit Media is a UGC content agency producing authentic creator content for D2C brands. Real UGC ads that convert on Meta, Instagram, and beyond."
        canonical="https://thriveitmedia.in/services/real-ugc"
        schema={[serviceSchema, faqSchema]}
      />

      {/* Hero */}
      <section className="py-32 md:py-40 relative">
        <div className="absolute inset-0 grid-pattern" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">Real UGC</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#F5F5F0] max-w-5xl mb-8">
              Real UGC Content Agency for D2C Brands
            </h1>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Real UGC */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-8">Why Real UGC Outperforms Polished Brand Content</h2>
            <p className="text-base md:text-lg font-body font-light text-[#888880] leading-relaxed max-w-3xl">
              People do not trust ads that look like ads. They trust real people. User-generated content — authentic content created by real creators using your actual product — is consistently the highest-performing creative format for D2C brands running paid ads. Consumers are 2.4 times more likely to say UGC is authentic compared to brand-created content. UGC-style ads consistently achieve lower cost per click and higher conversion rates on Meta.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* What We Produce */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">What We Produce</h2>
          </ScrollReveal>
          <StaggerContainer className="space-y-4">
            {whatWeProduce.map((item) => (
              <StaggerItem key={item} className="flex items-start gap-4 bg-[#111111] border border-white/5 p-6">
                <Check className="w-5 h-5 text-[#FF5C35] mt-0.5 flex-shrink-0" />
                <p className="text-base font-body font-light text-[#F5F5F0]">{item}</p>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">Our UGC Production Process</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step) => (
              <StaggerItem key={step.num}>
                <div data-testid={`step-${step.num}`} className="bg-[#0a0a0a] border border-white/5 p-8 group hover:border-[#FF5C35]/30 transition-all duration-500">
                  <span className="text-5xl font-heading font-extrabold text-white/5 group-hover:text-[#FF5C35]/20 transition-colors">{step.num}</span>
                  <h3 className="font-heading text-lg font-bold text-[#F5F5F0] mt-4 mb-2">{step.title}</h3>
                  <p className="text-sm font-body font-light text-[#888880] leading-relaxed">{step.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <CTABanner
        heading="Book a Free UGC Consultation"
        subtext="Let's discuss how authentic creator content can drive better results for your D2C brand's paid and organic channels."
        buttonText="Book a Free Consultation"
      />
    </>
  );
}
